import { useState, useCallback } from 'react'
import { Drug } from '@/features/internal/drugs/types/soapTemplate'
import { AdvancedSearchCondition } from '@/shared/types/advancedSearch'
import { useToast } from '@/shared/contexts/ToastContext'
import { DRUG_SELECTION } from '@/shared/constants/const'
import {
  useGroupDrugsQuery,
  useAddDrugsToGroupMutation,
  useRemoveDrugFromGroupMutation,
  useBulkDeleteDrugsFromGroupMutation,
  useFetchAllNormalDrugsMutation,
  useFetchAllAdvancedDrugsMutation,
} from './queries/useDrugGroupDrugsQuery'

/**
 * 医薬品グループの所属医薬品管理フック
 *
 * TanStack Queryに移行済み:
 * - groupDrugs取得はuseQuery（groupId変更時に自動refetch）
 * - 追加/削除/一括削除はuseMutation
 * - 検索結果と選択状態はローカルstate維持
 *
 * @param groupId - 医薬品グループID（必須）
 */
export const useDrugGroupDrugs = (groupId: string) => {
  // TanStack Query（groupId変更時に自動refetch）
  const {
    groupDrugs,
    isLoading: groupDrugsLoading,
    error: groupDrugsError,
  } = useGroupDrugsQuery(groupId)

  const addDrugsMutation = useAddDrugsToGroupMutation()
  const removeDrugMutation = useRemoveDrugFromGroupMutation()
  const bulkDeleteMutation = useBulkDeleteDrugsFromGroupMutation()
  const fetchAllNormalDrugsMutation = useFetchAllNormalDrugsMutation()
  const fetchAllAdvancedDrugsMutation = useFetchAllAdvancedDrugsMutation()

  // ローカル状態（選択状態）
  const [selectedDrugIds, setSelectedDrugIds] = useState<string[]>([])

  const { showToast } = useToast()

  // 統合ローディング・エラー状態
  const loading =
    groupDrugsLoading ||
    addDrugsMutation.isPending ||
    removeDrugMutation.isPending ||
    bulkDeleteMutation.isPending ||
    fetchAllNormalDrugsMutation.isPending ||
    fetchAllAdvancedDrugsMutation.isPending
  const error = groupDrugsError

  // 医薬品グループに医薬品を追加
  const addDrugsToGroupAction = useCallback(
    async (gId: string) => {
      if (selectedDrugIds.length === 0) {
        showToast('選択された医薬品がありません', 'warning')
        return false
      }

      try {
        await addDrugsMutation.mutateAsync({
          groupId: gId,
          drugIds: selectedDrugIds,
        })
        // 成功したら選択をクリア
        setSelectedDrugIds([])
        return true
      } catch {
        return false
      }
    },
    [selectedDrugIds, addDrugsMutation, showToast]
  )

  // 医薬品の選択状態を切り替え
  const toggleDrugSelection = useCallback((drugId: string) => {
    setSelectedDrugIds((prevIds) => {
      if (prevIds.includes(drugId)) {
        return prevIds.filter((id) => id !== drugId)
      } else {
        return [...prevIds, drugId]
      }
    })
  }, [])

  // 検索結果の全医薬品を選択
  const selectAllDrugs = useCallback((displayDrugs?: Drug[]) => {
    const drugsToSelect = displayDrugs || []
    const allDrugIds = drugsToSelect.map((drug) => drug.id)
    setSelectedDrugIds((prevIds) => {
      const combinedIds = new Set([...prevIds, ...allDrugIds])
      return Array.from(combinedIds)
    })
  }, [])

  // 検索キーワードに一致するすべての薬剤を選択（通常検索）
  const selectAllSearchResults = useCallback(
    async (searchKeyword: string) => {
      try {
        const result = await fetchAllNormalDrugsMutation.mutateAsync(
          searchKeyword
        )
        const allDrugs = result.drugs

        // 上限チェック
        if (allDrugs.length > DRUG_SELECTION.MAX_SELECTION_COUNT) {
          return {
            totalFound: allDrugs.length,
            selectedCount: 0,
            alreadyInGroup: 0,
          }
        }

        // グループ内の薬剤を除外する
        const groupDrugIds = new Set(groupDrugs.map((drug) => drug.id))
        const selectableDrugs = allDrugs.filter(
          (drug) => !groupDrugIds.has(drug.id)
        )

        const allDrugIds = selectableDrugs.map((drug) => drug.id)
        setSelectedDrugIds((prevIds) => {
          const combinedIds = new Set([...prevIds, ...allDrugIds])
          return Array.from(combinedIds)
        })

        return {
          totalFound: allDrugs.length,
          selectedCount: selectableDrugs.length,
          alreadyInGroup: allDrugs.length - selectableDrugs.length,
        }
      } catch {
        // エラーハンドリングは Mutation の onError で行う
        return {
          totalFound: 0,
          selectedCount: 0,
          alreadyInGroup: 0,
        }
      }
    },
    [groupDrugs, fetchAllNormalDrugsMutation]
  )

  // 高度検索条件に一致するすべての薬剤を選択
  const selectAllAdvancedSearchResults = useCallback(
    async (conditions: AdvancedSearchCondition[]) => {
      try {
        const result = await fetchAllAdvancedDrugsMutation.mutateAsync(
          conditions
        )
        const allDrugs = result.drugs

        // 上限チェック
        if (allDrugs.length > DRUG_SELECTION.MAX_SELECTION_COUNT) {
          return {
            totalFound: allDrugs.length,
            selectedCount: 0,
            alreadyInGroup: 0,
          }
        }

        // グループ内の薬剤を除外する
        const groupDrugIds = new Set(groupDrugs.map((drug) => drug.id))
        const selectableDrugs = allDrugs.filter(
          (drug) => !groupDrugIds.has(drug.id)
        )

        const allDrugIds = selectableDrugs.map((drug) => drug.id)
        setSelectedDrugIds((prevIds) => {
          const combinedIds = new Set([...prevIds, ...allDrugIds])
          return Array.from(combinedIds)
        })

        return {
          totalFound: allDrugs.length,
          selectedCount: selectableDrugs.length,
          alreadyInGroup: allDrugs.length - selectableDrugs.length,
        }
      } catch {
        // エラーハンドリングは Mutation の onError で行う
        return {
          totalFound: 0,
          selectedCount: 0,
          alreadyInGroup: 0,
        }
      }
    },
    [groupDrugs, fetchAllAdvancedDrugsMutation]
  )

  // 検索結果の全医薬品の選択を解除
  const deselectAllDrugs = useCallback((displayDrugs?: Drug[]) => {
    const drugsToDeselect = displayDrugs || []
    const drugsToDeselectIds = new Set(drugsToDeselect.map((drug) => drug.id))
    setSelectedDrugIds((prevIds) =>
      prevIds.filter((id) => !drugsToDeselectIds.has(id))
    )
  }, [])

  // 選択をすべてクリア
  const clearSelection = useCallback(() => {
    setSelectedDrugIds([])
  }, [])

  // 医薬品グループから医薬品を削除
  const removeDrugFromGroupAction = useCallback(
    async (gId: string, drugId: string) => {
      try {
        await removeDrugMutation.mutateAsync({ groupId: gId, drugId })
        return true
      } catch {
        return false
      }
    },
    [removeDrugMutation]
  )

  // 医薬品グループから複数の医薬品を一括削除
  const bulkDeleteDrugsFromGroupAction = useCallback(
    async (gId: string, drugIds: string[]) => {
      if (drugIds.length === 0) {
        showToast('削除する医薬品を選択してください', 'warning')
        return false
      }

      try {
        await bulkDeleteMutation.mutateAsync({ groupId: gId, drugIds })
        // 選択をクリア
        setSelectedDrugIds([])
        return true
      } catch {
        return false
      }
    },
    [bulkDeleteMutation, showToast]
  )

  return {
    // 状態
    groupDrugs,
    selectedDrugIds,
    loading,
    error,

    // アクション
    addDrugsToGroupAction,
    removeDrugFromGroupAction,
    bulkDeleteDrugsFromGroupAction,
    toggleDrugSelection,
    selectAllDrugs,
    selectAllSearchResults,
    selectAllAdvancedSearchResults,
    deselectAllDrugs,
    clearSelection,
    setSelectedDrugIds,
  }
}
